/**
 * ZITELOU - Statistics Page
 * Page de statistiques avancées et rapports
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  UsersIcon,
  CurrencyEuroIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Select, Loader } from '../components/Common';
import LineChart from '../components/Dashboard/LineChart';
import BarChart from '../components/Dashboard/BarChart';
import PieChart from '../components/Dashboard/PieChart';
import StatCard from '../components/Dashboard/StatCard';
import { useApi } from '../hooks';
import api from '../services/api';
import { formatCurrency, formatDate, exportToCSV } from '../utils';
import { toast } from 'react-toastify';

const Statistics = () => {
  const { loading, execute } = useApi();
  const [stats, setStats] = useState(null);
  const [growthData, setGrowthData] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [period, setPeriod] = useState('12months');

  const fetchStatistics = useCallback(async () => {
    await execute(async () => {
      const [statsResult, growthResult, cancellationsResult] = await Promise.all([
        api.getStats(),
        api.getGrowthData(period),
        api.getCancellations(),
      ]);

      setStats(statsResult);
      setGrowthData(growthResult);
      setCancellations(cancellationsResult);
    });
  }, [period, execute]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handleExport = () => {
    const dataToExport = growthData.map((item) => ({
      Mois: item.month,
      Utilisateurs: item.users,
      Abonnements: item.subscriptions,
      'Revenu (€)': item.revenue,
    }));

    exportToCSV(dataToExport, `statistiques-zitelou-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('Données exportées avec succès');
  };

  if (loading || !stats) {
    return <Loader fullScreen text="Chargement des statistiques..." />;
  }

  // Prepare chart data
  const usersGrowthData = {
    labels: growthData.map((d) => d.month),
    datasets: [
      {
        label: 'Nouveaux utilisateurs',
        data: growthData.map((d) => d.users),
        borderColor: 'rgb(1, 57, 134)',
        backgroundColor: 'rgba(1, 57, 134, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const subscriptionsGrowthData = {
    labels: growthData.map((d) => d.month),
    datasets: [
      {
        label: 'Abonnements actifs',
        data: growthData.map((d) => d.subscriptions),
        borderColor: 'rgb(150, 203, 132)',
        backgroundColor: 'rgba(150, 203, 132, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const revenueGrowthData = {
    labels: growthData.map((d) => d.month),
    datasets: [
      {
        label: 'Revenu (€)',
        data: growthData.map((d) => d.revenue),
        backgroundColor: 'rgba(255, 165, 0, 0.8)',
      },
    ],
  };

  const subscriptionTypesData = {
    labels: ['Mensuel', 'Annuel'],
    datasets: [
      {
        data: [stats.monthly_subscriptions, stats.yearly_subscriptions],
        backgroundColor: ['rgba(1, 57, 134, 0.8)', 'rgba(150, 203, 132, 0.8)'],
        borderColor: ['rgb(1, 57, 134)', 'rgb(150, 203, 132)'],
        borderWidth: 2,
      },
    ],
  };

  // Calculate conversion funnel
  const conversionFunnel = [
    { stage: 'Inscrits', value: stats.total_users, percentage: 100 },
    {
      stage: 'Abonnés',
      value: stats.active_subscriptions,
      percentage: Math.round((stats.active_subscriptions / stats.total_users) * 100),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Statistiques</h1>
          <p className="text-gray-600 mt-2">
            Analyses et rapports détaillés de la plateforme Zitelou
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            options={[
              { value: '6months', label: '6 derniers mois' },
              { value: '12months', label: '12 derniers mois' },
              { value: '24months', label: '24 derniers mois' },
            ]}
          />
          <Button
            variant="primary"
            leftIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
            onClick={handleExport}
          >
            Exporter
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Taux de Conversion"
          value={`${stats.conversion_rate}%`}
          icon={ChartBarIcon}
          iconBgColor="bg-purple/20"
          iconColor="text-purple"
        />

        <StatCard
          title="Taux de Résiliation"
          value={`${stats.churn_rate}%`}
          change={`${stats.cancelled_30d} ce mois`}
          changeType="neutral"
          icon={UsersIcon}
          iconBgColor="bg-danger/10"
          iconColor="text-danger"
        />

        <StatCard
          title="Durée Moyenne"
          value={`${stats.avg_subscription_duration} mois`}
          icon={ChartBarIcon}
          iconBgColor="bg-primary/20"
          iconColor="text-primary"
        />

        <StatCard
          title="MRR Total"
          value={formatCurrency(stats.mrr)}
          change={`+${stats.monthly_growth}%`}
          changeType="positive"
          icon={CurrencyEuroIcon}
          iconBgColor="bg-success/20"
          iconColor="text-success"
        />
      </div>

      {/* Growth Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Évolution des Utilisateurs" subtitle="Croissance mensuelle">
          <LineChart data={usersGrowthData} height={300} />
        </Card>

        <Card title="Évolution des Abonnements" subtitle="Abonnements actifs">
          <LineChart data={subscriptionsGrowthData} height={300} />
        </Card>
      </div>

      {/* Revenue & Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Revenu Mensuel" subtitle="En euros" className="lg:col-span-2">
          <BarChart data={revenueGrowthData} height={300} />
        </Card>

        <Card title="Répartition Abonnements" subtitle="Par type">
          <PieChart data={subscriptionTypesData} height={300} />
        </Card>
      </div>

      {/* Conversion Funnel */}
      <Card title="Tunnel de Conversion" subtitle="Du visiteur à l'abonné">
        <div className="space-y-4">
          {conversionFunnel.map((item, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{item.stage}</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {item.value.toLocaleString('fr-FR')}
                  </span>
                  <span className="text-sm text-gray-500">{item.percentage}%</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-primary h-4 rounded-full transition-all duration-500"
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Cancellation Reasons */}
      <Card
        title="Raisons de Résiliation"
        subtitle="30 derniers jours"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Parent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Durée
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Raison
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cancellations.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(item.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.parent}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.duration}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.reason}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.type === 'Annuel'
                          ? 'bg-success/20 text-success'
                          : 'bg-primary/20 text-primary'
                      }`}
                    >
                      {item.type}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Statistics;
